/**
 * ObjectSerializer - binary encode/decode for arbitrary objects with @property metadata.
 *
 * Shared between the replication pipeline ({@link ReplicationSerializer}) and the RPC
 * binary codec ({@link RpcBinaryCodec}). Contains the per-object serialize/deserialize
 * logic and the class-metadata cache; higher-level actor/component coordination lives in
 * each consumer.
 */
import { Actor } from '../actors/Actor.js';
import { ClassRegistry } from '../systems/ClassRegistry.js';
import { getPropertyMetadata } from '../utils/serialization/decorator.js';

import {
  readClassDiscriminator,
  writeClassDiscriminator,
} from './ReplicationBinaryCodec.js';

import type {
  BinaryReader,
  BinaryWriter} from './ReplicationBinaryCodec.js';
import type { NetPropertyMetadata, PropertyMetadata } from '../utils/serialization/decorator.js';

// ---------------------------------------------------------------------------
// ActorRef marker (shared across serializers)
// ---------------------------------------------------------------------------

/**
 * Marker used in JS-value state records to represent an actor reference.
 */
export interface ActorRef {
  $actorRef: string;
}

/** Check if a value is an actor-reference marker. */
export function isActorRef(value: unknown): value is ActorRef {
  return (
    value !== null &&
    typeof value === 'object' &&
    typeof (value as Record<string, unknown>).$actorRef === 'string'
  );
}

// ---------------------------------------------------------------------------
// Class metadata cache
// ---------------------------------------------------------------------------

/**
 * Per-class cached metadata used by both encode and decode paths.
 */
export interface ClassObjectMetadata {
  /** Ordered list of replicated property names (base → derived). */
  replicatedProps: string[];
  /** propName → `onRep*` callback name. */
  repCallbacks: Map<string, string>;
  /** propName → binary net metadata. Only props with a `net` block are included. */
  netMeta: Map<string, NetPropertyMetadata>;
}

const classMetadataCache = new Map<Function, ClassObjectMetadata>();

/** Return (and cache) the object metadata for a given class constructor. */
export function getClassObjectMetadata(classConstructor: Function): ClassObjectMetadata {
  let meta = classMetadataCache.get(classConstructor);
  if (!meta) {
    meta = buildClassObjectMetadata(classConstructor);
    classMetadataCache.set(classConstructor, meta);
  }
  return meta;
}

function buildClassObjectMetadata(classConstructor: Function): ClassObjectMetadata {
  const allMetadata = getPropertyMetadata(classConstructor.prototype);
  const replicatedProps: string[] = [];
  const repCallbacks = new Map<string, string>();
  const netMeta = new Map<string, NetPropertyMetadata>();

  for (const [propName, metadata] of Object.entries(allMetadata)) {
    const pm = metadata as PropertyMetadata;
    if (pm?.replicate !== true) continue;

    if (!pm.net) {
      console.warn(
        `[ObjectSerializer] Property "${propName}" on ${classConstructor.name} has replicate:true but no net block. ` +
        'Run the property metadata generator or add a net block manually. Skipping binary encoding for this property.'
      );
      continue;
    }

    replicatedProps.push(propName);
    netMeta.set(propName, pm.net);

    const callbackName = `onRep${propName.charAt(0).toUpperCase()}${propName.slice(1)}`;
    if (typeof classConstructor.prototype[callbackName] === 'function') {
      repCallbacks.set(propName, callbackName);
    }
  }

  // Reverse so base-class properties come first (stable ordering across client/server).
  replicatedProps.reverse();

  return { replicatedProps, repCallbacks, netMeta };
}

// ---------------------------------------------------------------------------
// Wire sentinel for delta-array encoding
// ---------------------------------------------------------------------------

/**
 * Written instead of a uint16 element count to signal delta-array mode.
 * 65535 elements is impractical for replication, so this value is safe to reserve.
 */
export const DELTA_ARRAY_SENTINEL = 0xFFFF as const;

// ---------------------------------------------------------------------------
// serializeObjectAndEncode
// ---------------------------------------------------------------------------

/**
 * Serialize all replicated properties of `obj` into `outState` (for next-tick delta
 * comparison) and encode changed ones into `writer` in a single pass.
 *
 * The bitmask is reserved as zero bytes at the start, individual values are written
 * inline as changes are detected, and the bitmask is patched in-place once all props
 * have been processed. Unchanged struct/class values are rolled back via `resetTo`.
 *
 * @param writer          Binary output buffer.
 * @param obj             Object to serialize (actor, component, or struct instance).
 * @param lastState       JS-value snapshot from the previous tick, or `null` for a full encode.
 * @param outState        Output record that receives the current JS-value snapshot.
 * @param isOwningClient  Whether the target is the owning client; controls `replicateTo` filtering.
 * @returns `true` if at least one property changed and was written.
 */
export function serializeObjectAndEncode(
  writer: BinaryWriter,
  obj: object,
  lastState: Record<string, unknown> | null,
  outState: Record<string, unknown>,
  isOwningClient: boolean = true,
): boolean {
  const { replicatedProps, netMeta } = getClassObjectMetadata(obj.constructor);
  const forceAll = lastState === null;

  const bitmaskOffset = writer.reserveBitmask(replicatedProps.length);
  const changedIndices = new Set<number>();

  for (let i = 0; i < replicatedProps.length; i++) {
    const propName = replicatedProps[i];
    const net = netMeta.get(propName);
    if (!net) continue;

    const replicateTo = net.replicateTo ?? 'all';
    if (replicateTo === 'owner' && !isOwningClient) continue;
    if (replicateTo === 'non-owner' && isOwningClient) continue;

    const liveValue = (obj as Record<string, unknown>)[propName];
    const lastSer = lastState?.[propName];

    const { serialized, changed } = encodeValueCombined(writer, liveValue, lastSer, net, forceAll);
    outState[propName] = serialized;
    if (changed) changedIndices.add(i);
  }

  writer.patchBitmask(bitmaskOffset, replicatedProps.length, changedIndices);
  return changedIndices.size > 0;
}

// ---------------------------------------------------------------------------
// decodeObjectFromBinary
// ---------------------------------------------------------------------------

/**
 * Decode replicated properties from `reader` into `obj`.
 * Fires `onRep*` callbacks after each property is applied.
 *
 * @param obj             Target object (actor, component, or struct instance).
 * @param resolveActorRef Optional resolver for actor UUID → Actor instance.
 * @param pathPrefix      Dot-path prefix used for dangling-ref tracking keys.
 * @param unresolvedRefs  Output map of dotPath → UUID for unresolvable actor references.
 */
export function decodeObjectFromBinary(
  reader: BinaryReader,
  obj: object,
  resolveActorRef: ((uuid: string) => Actor | null) | undefined,
  pathPrefix: string,
  unresolvedRefs: Map<string, string>,
): void {
  const metadata = getClassObjectMetadata(obj.constructor);
  const { replicatedProps, netMeta } = metadata;

  const present = reader.readBitmask(replicatedProps.length);

  for (let i = 0; i < replicatedProps.length; i++) {
    if (!present[i]) continue;
    const propName = replicatedProps[i];
    const net = netMeta.get(propName);
    if (!net) continue;

    const dotPath = pathPrefix ? `${pathPrefix}.${propName}` : propName;
    const currentValue = (obj as Record<string, unknown>)[propName];

    const decoded = decodeValueFromBinary(
      reader, currentValue, net, resolveActorRef, dotPath, unresolvedRefs,
    );

    if (decoded !== UNRESOLVED_SENTINEL) {
      (obj as Record<string, unknown>)[propName] = decoded;
      fireRepCallback(obj, metadata, propName);
    }
  }
}

// ---------------------------------------------------------------------------
// Private: combined encode
// ---------------------------------------------------------------------------

function encodeValueCombined(
  writer: BinaryWriter,
  value: unknown,
  lastSer: unknown,
  net: NetPropertyMetadata,
  forceAll: boolean,
): { serialized: unknown; changed: boolean } {
  if (net.nullable && (value === null || value === undefined)) {
    const changed = forceAll || lastSer !== null;
    if (changed) writer.writePresence(false);
    return { serialized: null, changed };
  }

  switch (net.netType) {
    case 'bool': {
      const v = value as boolean;
      const changed = forceAll || v !== lastSer;
      if (changed) {
        if (net.nullable) writer.writePresence(true);
        writer.writeBool(v);
      }
      return { serialized: v, changed };
    }

    case 'uint8':
    case 'int8':
    case 'uint16':
    case 'int16':
    case 'int32':
    case 'uint32':
    case 'float32':
    case 'float64': {
      const v = typeof value === 'number' ? value : 0;
      const changed = forceAll || v !== lastSer;
      if (changed) {
        if (net.nullable) writer.writePresence(true);
        switch (net.netType) {
          case 'uint8': writer.writeUint8(v); break;
          case 'int8': writer.writeInt8(v); break;
          case 'uint16': writer.writeUint16(v); break;
          case 'int16': writer.writeInt16(v); break;
          case 'int32': writer.writeInt32(v); break;
          case 'uint32': writer.writeUint32(v); break;
          case 'float32': writer.writeFloat32(v); break;
          case 'float64': writer.writeFloat64(v); break;
        }
      }
      return { serialized: v, changed };
    }

    case 'string': {
      const v = typeof value === 'string' ? value : '';
      const changed = forceAll || v !== lastSer;
      if (changed) {
        if (net.nullable) writer.writePresence(true);
        writer.writeString(v);
      }
      return { serialized: v, changed };
    }

    case 'enum': {
      const changed = forceAll || value !== lastSer;
      if (changed) {
        const opts = net.enumOptions ?? [];
        const idx = opts.indexOf(value as string);
        if (net.nullable) writer.writePresence(true);
        writer.writeUint8(idx >= 0 ? idx : 0);
      }
      return { serialized: value, changed };
    }

    case 'actorRef': {
      const uuid = value instanceof Actor ? value.uuid : (value as ActorRef)?.$actorRef ?? '';
      const ser: ActorRef = { $actorRef: uuid };
      const prevUuid = isActorRef(lastSer) ? lastSer.$actorRef : undefined;
      const changed = forceAll || prevUuid !== uuid;
      if (changed) {
        if (net.nullable) writer.writePresence(true);
        writer.writeActorId(uuid);
      }
      return { serialized: ser, changed };
    }

    case 'vector2': {
      const v = value as { x: number; y: number };
      const lastArr = Array.isArray(lastSer) ? lastSer as number[] : null;
      const serialized = [v.x, v.y];
      const changed = forceAll || !lastArr || lastArr[0] !== v.x || lastArr[1] !== v.y;
      if (changed) {
        if (net.nullable) writer.writePresence(true);
        writer.writeVector2(v);
      }
      return { serialized, changed };
    }

    case 'vector3':
    case 'euler': {
      const v = value as { x: number; y: number; z: number };
      const lastArr = Array.isArray(lastSer) ? lastSer as number[] : null;
      const serialized = [v.x, v.y, v.z];
      const changed = forceAll || !lastArr || lastArr[0] !== v.x || lastArr[1] !== v.y || lastArr[2] !== v.z;
      if (changed) {
        if (net.nullable) writer.writePresence(true);
        if (net.netType === 'euler') writer.writeEuler(v); else writer.writeVector3(v);
      }
      return { serialized, changed };
    }

    case 'vector4':
    case 'quaternion': {
      const v = value as { x: number; y: number; z: number; w: number };
      const lastArr = Array.isArray(lastSer) ? lastSer as number[] : null;
      const serialized = [v.x, v.y, v.z, v.w];
      const changed = forceAll || !lastArr
        || lastArr[0] !== v.x || lastArr[1] !== v.y || lastArr[2] !== v.z || lastArr[3] !== v.w;
      if (changed) {
        if (net.nullable) writer.writePresence(true);
        writer.writeVector4(v);
      }
      return { serialized, changed };
    }

    case 'struct':
    case 'class': {
      if (value === null || value === undefined) {
        const changed = forceAll || lastSer !== null;
        if (changed) {
          if (net.nullable) {
            writer.writePresence(false);
          } else if (net.netType === 'class' && net.polymorphic) {
            writer.writeUint32(0);
          } else {
            const meta = net.structClass ? getClassObjectMetadata(net.structClass) : null;
            if (meta) writer.reserveBitmask(meta.replicatedProps.length);
          }
        }
        return { serialized: null, changed };
      }

      const lastNested = (!forceAll && lastSer !== null && lastSer !== undefined
        && typeof lastSer === 'object' && !Array.isArray(lastSer))
        ? lastSer as Record<string, unknown>
        : null;

      const nestedState: Record<string, unknown> = {};
      const beforeOffset = writer.offset;

      if (net.nullable) writer.writePresence(true);
      if (net.netType === 'class' && net.polymorphic) writeClassDiscriminator(writer, value as object);

      const nestedChanged = serializeObjectAndEncode(
        writer, value as object, forceAll ? null : lastNested, nestedState,
      );

      if (!nestedChanged && !forceAll) {
        writer.resetTo(beforeOffset);
        return { serialized: nestedState, changed: false };
      }
      return { serialized: nestedState, changed: true };
    }

    case 'array': {
      const arr = Array.isArray(value) ? value : [];
      const lastArr = !forceAll && Array.isArray(lastSer) ? lastSer as unknown[] : null;
      const lengthChanged = lastArr === null || arr.length !== lastArr.length;
      const canDelta = !forceAll && !lengthChanged;

      const serialized: unknown[] = new Array(arr.length);

      if (canDelta) {
        const beforeArray = writer.offset;
        if (net.nullable) writer.writePresence(true);
        writer.writeUint16(DELTA_ARRAY_SENTINEL);
        const bitmaskOffset = writer.reserveBitmask(arr.length);
        const changedIndices = new Set<number>();

        for (let i = 0; i < arr.length; i++) {
          const elemBefore = writer.offset;
          const { serialized: elemSer, changed: elemChanged } =
            net.element
              ? encodeValueCombined(writer, arr[i], lastArr![i], net.element, false)
              : { serialized: arr[i], changed: false };
          serialized[i] = elemSer;
          if (elemChanged) {
            changedIndices.add(i);
          } else {
            writer.resetTo(elemBefore);
          }
        }

        if (changedIndices.size === 0) {
          writer.resetTo(beforeArray);
          return { serialized, changed: false };
        }

        writer.patchBitmask(bitmaskOffset, arr.length, changedIndices);
        return { serialized, changed: true };
      }

      let anyChanged = forceAll || lengthChanged;
      if (net.nullable) writer.writePresence(true);
      writer.writeUint16(arr.length);
      for (let i = 0; i < arr.length; i++) {
        const { serialized: elemSer, changed: elemChanged } =
          net.element
            ? encodeValueCombined(writer, arr[i], lastArr?.[i] ?? null, net.element, forceAll)
            : { serialized: arr[i], changed: false };
        serialized[i] = elemSer;
        if (elemChanged) anyChanged = true;
      }
      return { serialized, changed: anyChanged };
    }

    default:
      console.warn(`[ObjectSerializer] Unknown netType: ${(net as NetPropertyMetadata).netType}`);
      return { serialized: value, changed: false };
  }
}

// ---------------------------------------------------------------------------
// Private: binary decode
// ---------------------------------------------------------------------------

// Sentinel returned for unresolved actor references; callers must NOT write it to the object.
const UNRESOLVED_SENTINEL: unique symbol = Symbol('UNRESOLVED_ACTOR_REF');

function decodeValueFromBinary(
  reader: BinaryReader,
  currentValue: unknown,
  net: NetPropertyMetadata,
  resolveActorRef: ((uuid: string) => Actor | null) | undefined,
  dotPath: string,
  unresolvedRefs: Map<string, string>,
): unknown {
  if (net.nullable) {
    if (!reader.readPresence()) return null;
  }

  switch (net.netType) {
    case 'bool': return reader.readBool();
    case 'uint8': return reader.readUint8();
    case 'int8': return reader.readInt8();
    case 'uint16': return reader.readUint16();
    case 'int16': return reader.readInt16();
    case 'int32': return reader.readInt32();
    case 'uint32': return reader.readUint32();
    case 'float32': return reader.readFloat32();
    case 'float64': return reader.readFloat64();
    case 'string': return reader.readString();

    case 'enum': {
      const idx = reader.readUint8();
      const opts = net.enumOptions ?? [];
      return opts[idx] ?? opts[0] ?? '';
    }

    case 'actorRef': {
      const uuid = reader.readActorId();
      if (!uuid) return null;
      if (resolveActorRef) {
        const resolved = resolveActorRef(uuid);
        if (resolved) return resolved;
      }
      unresolvedRefs.set(dotPath, uuid);
      return UNRESOLVED_SENTINEL;
    }

    case 'vector2': {
      const raw = reader.readVector2();
      if (currentValue !== null && currentValue !== undefined &&
        typeof (currentValue as { fromArray?: unknown }).fromArray === 'function') {
        (currentValue as { fromArray(arr: number[]): void }).fromArray([raw.x, raw.y]);
        return currentValue;
      }
      return raw;
    }

    case 'vector3': {
      const raw = reader.readVector3();
      if (currentValue !== null && currentValue !== undefined &&
        typeof (currentValue as { fromArray?: unknown }).fromArray === 'function') {
        (currentValue as { fromArray(arr: number[]): void }).fromArray([raw.x, raw.y, raw.z]);
        return currentValue;
      }
      return raw;
    }

    case 'euler': {
      const raw = reader.readEuler();
      if (currentValue !== null && currentValue !== undefined &&
        typeof (currentValue as { fromArray?: unknown }).fromArray === 'function') {
        (currentValue as { fromArray(arr: number[]): void }).fromArray([raw.x, raw.y, raw.z]);
        return currentValue;
      }
      return raw;
    }

    case 'vector4':
    case 'quaternion': {
      const raw = reader.readVector4();
      if (currentValue !== null && currentValue !== undefined &&
        typeof (currentValue as { fromArray?: unknown }).fromArray === 'function') {
        (currentValue as { fromArray(arr: number[]): void }).fromArray([raw.x, raw.y, raw.z, raw.w]);
        return currentValue;
      }
      return raw;
    }

    case 'array': {
      const firstWord = reader.readUint16();
      const existingArr = Array.isArray(currentValue) ? currentValue : [];

      if (firstWord === DELTA_ARRAY_SENTINEL) {
        const count = existingArr.length;
        const present = reader.readBitmask(count);
        const result = [...existingArr];
        for (let i = 0; i < count; i++) {
          if (!present[i]) continue;
          if (net.element) {
            const elem = decodeValueFromBinary(
              reader, existingArr[i] ?? null, net.element,
              resolveActorRef, `${dotPath}.[${i}]`, unresolvedRefs,
            );
            result[i] = elem === UNRESOLVED_SENTINEL ? null : elem;
          }
        }
        return result;
      }

      const result: unknown[] = [];
      for (let i = 0; i < firstWord; i++) {
        if (net.element) {
          const elem = decodeValueFromBinary(
            reader, existingArr[i] ?? null, net.element,
            resolveActorRef, `${dotPath}.[${i}]`, unresolvedRefs,
          );
          result.push(elem === UNRESOLVED_SENTINEL ? null : elem);
        }
      }
      return result;
    }

    case 'struct': {
      let instance = currentValue;
      if ((instance === null || instance === undefined) && net.structClass) {
        instance = new (net.structClass as new () => object)();
      }
      if (instance === null || instance === undefined) {
        throw new Error(
          `[ObjectSerializer] Cannot decode struct for property "${dotPath}": ` +
          'no current instance and no structClass constructor in net metadata.',
        );
      }
      decodeObjectFromBinary(reader, instance as object, resolveActorRef, dotPath, unresolvedRefs);
      return instance;
    }

    case 'class': {
      let ctor: (new (...args: any[]) => any) | null = null;
      if (net.polymorphic) {
        ctor = readClassDiscriminator(reader);
        if (!ctor) {
          throw new Error(
            `[ObjectSerializer] Unknown polymorphic class hash for property "${dotPath}". ` +
            'Packet is corrupt or the class was removed from ClassRegistry.',
          );
        }
      } else if (net.netBaseClass) {
        try {
          ctor = ClassRegistry.getClass(net.netBaseClass);
        } catch {
          throw new Error(
            `[ObjectSerializer] Class "${net.netBaseClass}" not found in ClassRegistry for property "${dotPath}". ` +
            'Ensure the class is registered before receiving data.',
          );
        }
      }

      let instance = currentValue;
      if (ctor && (instance === null || instance === undefined || !(instance instanceof ctor))) {
        instance = new ctor();
      }
      if (instance === null || instance === undefined) {
        throw new Error(
          `[ObjectSerializer] Cannot decode class for property "${dotPath}": ` +
          'no constructor resolved.',
        );
      }
      decodeObjectFromBinary(reader, instance as object, resolveActorRef, dotPath, unresolvedRefs);
      return instance;
    }

    default:
      console.warn(`[ObjectSerializer] Unknown netType in decode: ${(net as NetPropertyMetadata).netType}`);
      return currentValue;
  }
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function fireRepCallback(
  obj: object,
  metadata: ClassObjectMetadata,
  propName: string,
): void {
  const callbackName = metadata.repCallbacks.get(propName);
  if (callbackName && typeof (obj as Record<string, unknown>)[callbackName] === 'function') {
    (obj as Record<string, () => void>)[callbackName]();
  }
}
